/**
 * ResearchedKeywordsPanel - Sortable table of keyword clusters from topical maps.
 *
 * Displays clusters as sortable table rows with expandable keyword previews.
 * Extracted from ContentPlannerPage.tsx per decomposition guidelines.
 *
 * The collapsed table uses each cluster's 5-item `keywords_preview` (cheap, from
 * the map summary). When a row is EXPANDED we lazily fetch the cluster's FULL
 * keyword list via the uncapped detail endpoint so nothing is cut off — see
 * ExpandedClusterKeywords below.
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ChevronDown, ChevronUp, Plus, Check, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { contentPlannerService } from '@/services/contentPlannerService';
import {
  getKeywordGrade,
  GRADE_COLORS,
  INTENT_LABELS,
  INTENT_FULL_LABELS,
  FRUIT_TIER_LABELS,
  FRUIT_TIER_COLORS,
} from '@/types/contentPlanner';
import type { ClusterSummary, ClusterDetail, FruitTier } from '@/types/contentPlanner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ResearchedKeywordsPanelProps {
  mapId: string;
  tenantId: string | null;
  onAddKeyword: (keyword: string) => void;
  onAddKeywords: (keywords: string[]) => void;
  currentKeywordsSet: Set<string>;
  maxClusters?: number;
}

type SortField = 'name' | 'primary_intent' | 'keyword_count' | 'total_volume' | 'avg_difficulty' | 'opportunity_score';

interface SortConfig {
  field: SortField;
  direction: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MAX_CLUSTERS = 12;
const CLUSTER_DETAIL_STALE_MS = 5 * 60 * 1000;

/** Dedupe keywords (case-insensitive), keeping the cluster name first. */
function dedupeKeywords(name: string | undefined, keywords: (string | undefined)[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const kw of [name, ...keywords]) {
    if (kw && !seen.has(kw.toLowerCase())) {
      seen.add(kw.toLowerCase());
      result.push(kw);
    }
  }
  return result;
}

/** Cheap preview keywords (max 5) straight from the cluster summary. */
function getClusterKeywords(cluster: ClusterSummary): string[] {
  return dedupeKeywords(cluster.name, cluster.keywords_preview || []);
}

/** Shared React Query key so the expanded view and the add-buttons share cache. */
function clusterDetailKey(tenantId: string | null, mapId: string, clusterId: string) {
  return ['cluster-detail', tenantId, mapId, clusterId] as const;
}

function allKeywordsAdded(keywords: string[], added: Set<string>): boolean {
  return keywords.length > 0 && keywords.every(kw => added.has(kw.toLowerCase()));
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SortIcon: React.FC<{ field: SortField; current: SortConfig }> = ({ field, current }) => {
  if (current.field !== field) return null;
  return current.direction === 'asc' ? (
    <ChevronUp className="inline h-3.5 w-3.5 ml-0.5" />
  ) : (
    <ChevronDown className="inline h-3.5 w-3.5 ml-0.5" />
  );
};

/**
 * Renders the FULL keyword list for an expanded cluster. Fetches the cluster
 * detail (uncapped) on mount; falls back to the 5-item preview while loading or
 * if the fetch fails, so the pills never disappear.
 */
const ExpandedClusterKeywords: React.FC<{
  mapId: string;
  tenantId: string | null;
  cluster: ClusterSummary;
  currentKeywordsSet: Set<string>;
  onAddKeyword: (keyword: string) => void;
}> = ({ mapId, tenantId, cluster, currentKeywordsSet, onAddKeyword }) => {
  const { data, isLoading, isError } = useQuery<ClusterDetail>({
    queryKey: clusterDetailKey(tenantId, mapId, cluster.id),
    queryFn: () => contentPlannerService.getCluster(mapId, cluster.id),
    enabled: !!mapId && !!cluster.id,
    staleTime: CLUSTER_DETAIL_STALE_MS,
  });

  const previewKws = getClusterKeywords(cluster);
  const fullKws = data
    ? dedupeKeywords(cluster.name, data.keywords?.map(k => k.keyword) || [])
    : previewKws;

  // While the full list loads, show the preview so pills never vanish.
  const keywords = fullKws.length > 0 ? fullKws : previewKws;
  const showLoading = isLoading && keywords.length <= previewKws.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {keywords.map((kw, idx) => {
        const isAdded = currentKeywordsSet.has(kw.toLowerCase());
        return (
          <button
            key={`${kw}-${idx}`}
            onClick={() => !isAdded && onAddKeyword(kw)}
            disabled={isAdded}
            className={cn(
              'px-1.5 py-0.5 rounded text-[10px] transition-colors',
              isAdded
                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 cursor-default'
                : 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 hover:bg-blue-100 cursor-pointer',
            )}
            title={isAdded ? 'Already added' : 'Click to add'}
          >
            {kw}
          </button>
        );
      })}
      {showLoading && (
        <span className="inline-flex items-center text-[10px] text-muted-foreground">
          <RefreshCw className="h-3 w-3 animate-spin mr-1" />
          loading all {cluster.keyword_count ?? ''} keywords…
        </span>
      )}
      {isError && (
        <span className="text-[10px] text-muted-foreground">
          (showing preview — could not load full list)
        </span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ResearchedKeywordsPanel: React.FC<ResearchedKeywordsPanelProps> = ({
  mapId,
  tenantId,
  onAddKeyword,
  onAddKeywords,
  currentKeywordsSet,
  maxClusters = MAX_CLUSTERS,
}) => {
  const queryClient = useQueryClient();
  const [sort, setSort] = useState<SortConfig>({ field: 'total_volume', direction: 'desc' });
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { data: mapDetail, isLoading } = useQuery({
    queryKey: ['topical-map', tenantId, mapId],
    queryFn: () => contentPlannerService.getMap(mapId),
    enabled: !!tenantId && !!mapId,
  });

  // ---- Sorting ----
  const handleSort = (field: SortField) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const allClusters = mapDetail?.clusters || [];

  const sortedClusters = useMemo(() => {
    const items = [...allClusters];

    items.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sort.field) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'primary_intent':
          aVal = a.primary_intent || '';
          bVal = b.primary_intent || '';
          break;
        case 'keyword_count':
          aVal = a.keyword_count ?? 0;
          bVal = b.keyword_count ?? 0;
          break;
        case 'total_volume':
          aVal = a.total_volume ?? 0;
          bVal = b.total_volume ?? 0;
          break;
        case 'avg_difficulty':
          aVal = a.avg_difficulty ?? 0;
          bVal = b.avg_difficulty ?? 0;
          break;
        case 'opportunity_score':
          aVal = a.opportunity_score ?? 0;
          bVal = b.opportunity_score ?? 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return items.slice(0, maxClusters);
  }, [allClusters, sort, maxClusters]);

  // ---- Expand / collapse ----
  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ---- Add the FULL keyword list of a single cluster (fetches detail on demand) ----
  const handleAddClusterKeywords = async (cluster: ClusterSummary) => {
    try {
      const detail = await queryClient.fetchQuery<ClusterDetail>({
        queryKey: clusterDetailKey(tenantId, mapId, cluster.id),
        queryFn: () => contentPlannerService.getCluster(mapId, cluster.id),
        staleTime: CLUSTER_DETAIL_STALE_MS,
      });
      onAddKeywords(dedupeKeywords(cluster.name, detail.keywords?.map(k => k.keyword) || []));
    } catch {
      // Fall back to the preview keywords if the detail fetch fails.
      onAddKeywords(getClusterKeywords(cluster));
    }
  };

  // ---- Aggregate keyword stats (preview-based; cheap summary of what's available) ----
  const allKeywords = useMemo(() => {
    const kws = new Set<string>();
    allClusters.forEach(c => {
      if (c.name) kws.add(c.name);
      c.keywords_preview?.forEach(kw => { if (kw) kws.add(kw); });
    });
    return Array.from(kws);
  }, [allClusters]);

  const notAddedKeywords = allKeywords.filter(kw => !currentKeywordsSet.has(kw.toLowerCase()));

  // ---- Loading / empty states ----
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
        <span className="text-sm text-muted-foreground">Loading keywords...</span>
      </div>
    );
  }

  if (!mapDetail) {
    return (
      <div className="text-center py-4 text-sm text-muted-foreground">
        Failed to load keywords
      </div>
    );
  }

  if (allClusters.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-muted-foreground">
        No clusters found in this research session
      </div>
    );
  }

  // ---- Render ----
  return (
    <div className="space-y-2">
      {/* Summary + Add all */}
      {notAddedKeywords.length > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {allKeywords.length} keywords available
            {notAddedKeywords.length < allKeywords.length && (
              <span className="text-green-600 dark:text-green-400 ml-1">
                ({allKeywords.length - notAddedKeywords.length} already added)
              </span>
            )}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddKeywords(notAddedKeywords)}
            className="text-xs h-7"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add all {notAddedKeywords.length}
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="max-h-[340px] overflow-y-auto rounded-md border">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            <TableRow>
              <TableHead className="w-8 px-2" />
              <TableHead
                className="cursor-pointer hover:bg-muted/50 text-xs"
                onClick={() => handleSort('name')}
              >
                Cluster
                <SortIcon field="name" current={sort} />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50 text-xs w-14 text-center"
                onClick={() => handleSort('primary_intent')}
              >
                Intent
                <SortIcon field="primary_intent" current={sort} />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50 text-xs w-16 text-right"
                onClick={() => handleSort('keyword_count')}
              >
                KWs
                <SortIcon field="keyword_count" current={sort} />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50 text-xs w-20 text-right"
                onClick={() => handleSort('total_volume')}
              >
                Volume
                <SortIcon field="total_volume" current={sort} />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50 text-xs w-16 text-right"
                onClick={() => handleSort('avg_difficulty')}
              >
                KD
                <SortIcon field="avg_difficulty" current={sort} />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/50 text-xs w-28 text-center"
                onClick={() => handleSort('opportunity_score')}
              >
                Opportunity
                <SortIcon field="opportunity_score" current={sort} />
              </TableHead>
              <TableHead className="w-10 px-2" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedClusters.map(cluster => {
              const clusterKws = getClusterKeywords(cluster);
              if (clusterKws.length === 0) return null;

              const isExpanded = expandedIds.has(cluster.id);
              const allAdded = allKeywordsAdded(clusterKws, currentKeywordsSet);
              const grade = cluster.avg_difficulty != null ? getKeywordGrade(cluster.avg_difficulty) : null;
              const intentLabel = cluster.primary_intent ? INTENT_LABELS[cluster.primary_intent] : null;
              const intentFull = cluster.primary_intent ? INTENT_FULL_LABELS[cluster.primary_intent] : '';

              return (
                <React.Fragment key={cluster.id}>
                  {/* Cluster row */}
                  <TableRow
                    className={cn(
                      'cursor-pointer transition-colors',
                      allAdded && 'bg-green-50 dark:bg-green-950/40',
                      isExpanded && 'border-b-0',
                    )}
                    onClick={() => toggleExpand(cluster.id)}
                  >
                    <TableCell className="px-2 py-1.5">
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell className="py-1.5 text-xs font-medium truncate max-w-[200px]">
                      {cluster.name}
                    </TableCell>
                    <TableCell className="py-1.5 text-center">
                      {intentLabel && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 h-4"
                          title={intentFull}
                        >
                          {intentLabel}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-1.5 text-xs text-right text-muted-foreground">
                      {cluster.keyword_count ?? '-'}
                    </TableCell>
                    <TableCell className="py-1.5 text-xs text-right">
                      {cluster.total_volume?.toLocaleString() ?? '0'}
                    </TableCell>
                    <TableCell className="py-1.5 text-xs text-right">
                      <span className="inline-flex items-center gap-1">
                        {cluster.avg_difficulty?.toFixed(0) ?? '-'}
                        {grade && (
                          <span className={cn(
                            'px-1 py-0 rounded text-[9px] font-bold leading-tight',
                            GRADE_COLORS[grade],
                          )}>
                            {grade}
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="py-1.5 text-center">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-xs font-medium tabular-nums">
                          {cluster.opportunity_score}
                        </span>
                        <span className={cn(
                          'px-1.5 py-0 rounded text-[9px] font-medium leading-tight border',
                          FRUIT_TIER_COLORS[cluster.fruit_tier as FruitTier],
                        )}>
                          {FRUIT_TIER_LABELS[cluster.fruit_tier as FruitTier]}
                        </span>
                      </span>
                    </TableCell>
                    <TableCell className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
                      {allAdded ? (
                        <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0"
                          onClick={() => handleAddClusterKeywords(cluster)}
                          title="Add all keywords from this cluster"
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded keyword pills — full, uncapped list */}
                  {isExpanded && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell />
                      <TableCell colSpan={7} className="pt-0 pb-2 pl-0">
                        <ExpandedClusterKeywords
                          mapId={mapId}
                          tenantId={tenantId}
                          cluster={cluster}
                          currentKeywordsSet={currentKeywordsSet}
                          onAddKeyword={onAddKeyword}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {allClusters.length > maxClusters && (
        <p className="text-xs text-center text-muted-foreground">
          Showing {maxClusters} of {allClusters.length} clusters
        </p>
      )}
    </div>
  );
};
